import { useId } from 'react';

import { type Content, POST_PLATFORMS, type Post, type PostInput, type PostPlatform } from '../api.ts';
import { fromLocalInputValue, toLocalInputValue } from '../schedule.ts';
import * as wording from '../wording.ts';
import { defaultPostFormat, defaultPostPlatform } from './posting.ts';

/**
 * The fields of one post as the operator types them (Design reference
 * § Dialogs, ScheduleDialog): ScheduleDialog draws them in a dialog, the posts
 * panel (US-024) inline, for a new post and to edit one.
 */
export interface PostDraft {
  readonly platform: PostPlatform;
  readonly paid: boolean;
  readonly label: string;
  readonly format: string | null;
  /** A `datetime-local` value; empty for no date. */
  readonly when: string;
  readonly notes: string;
  readonly caption: string;
}

/** Tomorrow at 09:00 here, or `day` at 09:00, as a `datetime-local` value. */
export function nineOClock(day?: string): string {
  if (day !== undefined) return `${day}T09:00`;
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return toLocalInputValue(date.toISOString());
}

/** A new post: the first share-copy platform without a post, its format, tomorrow (or `day`) at 09:00. */
export function newPostDraft(
  content: Pick<Content, 'formats' | 'shareCopy'>,
  posts: readonly Post[],
  day?: string,
): PostDraft {
  const platform = defaultPostPlatform(content, posts);
  return {
    platform,
    paid: false,
    label: '',
    format: defaultPostFormat(platform, content.formats),
    when: nineOClock(day),
    notes: '',
    caption: '',
  };
}

/** A planned post as the form edits it. */
export function draftOfPost(post: Post): PostDraft {
  return {
    platform: post.platform,
    paid: post.paid,
    label: post.label ?? '',
    format: post.format,
    when: post.scheduledAt === null ? '' : toLocalInputValue(post.scheduledAt),
    notes: post.notes ?? '',
    caption: post.caption ?? '',
  };
}

/** What the server takes: texts trimmed, an empty one and an empty date as `null`. */
export function postInputOf(draft: PostDraft, withCaption = false): PostInput {
  const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());
  return {
    platform: draft.platform,
    paid: draft.paid,
    label: text(draft.label),
    format: draft.format,
    scheduledAt: draft.when === '' ? null : fromLocalInputValue(draft.when),
    notes: text(draft.notes),
    ...(withCaption ? { caption: text(draft.caption) } : {}),
  };
}

/** Other goes by its label, so it needs one. */
export function draftLabelMissing(draft: PostDraft): boolean {
  return draft.platform === 'other' && draft.label.trim() === '';
}

/**
 * Platform (a change picks that platform's format), **Paid ad**, label,
 * format (only when the content has formats), date and time, notes, and the
 * caption when `withCaption` (editing a planned post).
 */
export function PostFields({
  draft,
  formats,
  withCaption = false,
  onChange,
}: {
  readonly draft: PostDraft;
  readonly formats: readonly string[];
  readonly withCaption?: boolean;
  readonly onChange: (draft: PostDraft) => void;
}) {
  const id = useId();
  const set = (patch: Partial<PostDraft>): void => {
    onChange({ ...draft, ...patch });
  };

  return (
    <>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-platform`}>
          {wording.POST_PLATFORM}
        </label>
        <select
          id={`${id}-platform`}
          className="field__input"
          value={draft.platform}
          onChange={(event) => {
            const platform = event.target.value as PostPlatform;
            set({ platform, format: defaultPostFormat(platform, formats) });
          }}
        >
          {POST_PLATFORMS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={draft.paid}
          onChange={(event) => {
            set({ paid: event.target.checked });
          }}
        />
        {wording.POST_PAID}
      </label>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-label`}>
          {wording.POST_LABEL}
        </label>
        <input
          id={`${id}-label`}
          className="field__input"
          type="text"
          maxLength={80}
          required={draft.platform === 'other'}
          value={draft.label}
          onChange={(event) => {
            set({ label: event.target.value });
          }}
        />
        <p className="field__hint">{wording.POST_LABEL_HINT}</p>
      </div>
      {formats.length > 0 && (
        <div className="field">
          <label className="field__label" htmlFor={`${id}-format`}>
            {wording.POST_FORMAT}
          </label>
          <select
            id={`${id}-format`}
            className="field__input"
            value={draft.format ?? ''}
            onChange={(event) => {
              set({ format: event.target.value === '' ? null : event.target.value });
            }}
          >
            <option value="">{wording.POST_NO_FORMAT}</option>
            {formats.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="field">
        <label className="field__label" htmlFor={`${id}-when`}>
          {wording.POST_WHEN}
        </label>
        <input
          id={`${id}-when`}
          className="field__input"
          type="datetime-local"
          value={draft.when}
          onChange={(event) => {
            set({ when: event.target.value });
          }}
        />
      </div>
      {withCaption && (
        <div className="field">
          <label className="field__label" htmlFor={`${id}-caption`}>
            {wording.POST_CAPTION}
          </label>
          <textarea
            id={`${id}-caption`}
            className="field__input field__textarea"
            rows={4}
            maxLength={5000}
            value={draft.caption}
            onChange={(event) => {
              set({ caption: event.target.value });
            }}
          />
          <p className="field__hint">{wording.POST_CAPTION_HINT}</p>
        </div>
      )}
      <div className="field">
        <label className="field__label" htmlFor={`${id}-notes`}>
          {wording.POST_NOTES}
        </label>
        <textarea
          id={`${id}-notes`}
          className="field__input field__textarea"
          rows={2}
          maxLength={1000}
          value={draft.notes}
          onChange={(event) => {
            set({ notes: event.target.value });
          }}
        />
      </div>
    </>
  );
}
